import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

import PriorityLinks, { CreateListingMenuLink } from './PriorityLinks';
import LinksMenu from './LinksMenu';

import css from './CustomLinksMenu.module.css';

// Persists across component remounts (SPA navigations) — same pattern as TopbarDesktop.
// Starts as false on the initial server render; becomes true after the first client mount.
let hasCustomLinksMenuHydratedOnce = false;

// Caches the link configs once PriorityLinks has measured their DOM widths.
// On SPA remount the state initialises from this cache so the off-screen portal
// measurement phase is skipped and the links are visible immediately.
// Stored as { key: string, links: Array } so the cache is invalidated when the
// visible link composition changes (auth state, user type, or custom links).
let measuredLinksCache = null;

// Layout effects run before the browser paints; the server renders no effects and keeps useEffect
// (no useLayoutEffect warning there).
const useLayoutEffectOnClient = typeof window === 'undefined' ? useEffect : useLayoutEffect;

// True when no web font is still loading. Widths measured while fonts are loading use the
// fallback font (e.g. 129px instead of 138px for a bold link), so such measurements are redone
// once document.fonts.ready resolves. Without the Font Loading API, measurements are kept.
const areFontsLoaded = () =>
  typeof document === 'undefined' || !document.fonts || document.fonts.status === 'loaded';

// Link widths come from offsetWidth, which rounds to whole pixels: a row that fits exactly by the
// measured numbers can be up to 0.5px per item wider on screen and touch the next topbar element.
// 1px is reserved per item in the row (links and the "More" label).
const ROUNDING_ALLOWANCE_PER_ITEM = 1;

// Fractional rendered width (offsetWidth would round it).
const renderedWidth = node =>
  node?.getBoundingClientRect ? node.getBoundingClientRect().width : node?.offsetWidth || 0;

const isSameLayout = (a, b) =>
  a.containerWidth === b.containerWidth &&
  a.priorityLinks.length === b.priorityLinks.length &&
  a.menuLinks.length === b.menuLinks.length &&
  a.priorityLinks.every((l, i) => l === b.priorityLinks[i]);

// Draft listing-creation contract for the client "Создать задачу" action.
// Mirrors TopbarMobileMenu's createTaskAction exactly (same route name, draft
// UUID params and ?type=tasks query) so the desktop and mobile client actions
// resolve to the identical route:
// /l/draft/00000000-0000-0000-0000-000000000000/new/details?type=tasks
// This inline action is only shown for the client (showInlineCreateListing =
// isAuthenticated && isCustomer in TopbarDesktop), so the task contract is
// applied for the client without affecting the provider create-service CTA.
const CREATE_TASK_PARAMS = {
  slug: 'draft',
  id: '00000000-0000-0000-0000-000000000000',
  type: 'new',
  tab: 'details',
};

const createListingLinkConfigMaybe = (intl, showLink, postListingLabelId) =>
  showLink
    ? [
        {
          group: 'primary',
          text: intl.formatMessage({ id: postListingLabelId }),
          type: 'internal',
          route: {
            name: 'EditListingPage',
            params: CREATE_TASK_PARAMS,
            to: { search: '?type=tasks' },
          },
          secondaryCta: true,
        },
      ]
    : [];

// True when every link is primary and the whole measured row fits: then no "More" label is needed.
const isWholeRowFitting = (links, containerWidth) => {
  const lastLink = links?.[links.length - 1];
  return (
    !!links?.[0]?.width &&
    links.every(l => l.group === 'primary') &&
    lastLink.cumulatedWidth + links.length * ROUNDING_ALLOWANCE_PER_ITEM <= containerWidth
  );
};

/**
 * Group links to 2 groups:
 * - priorityLinks (Those primary links that fit into current width of the TopbarDesktop.)
 * - menuLinks (The rest of the links that are shown inside dropdown menu.)
 *
 * @param {*} links array of link configs in an order where primary group is shown first
 * @param {*} containerWidth width reserved for the CustomLinksMenu component
 * @param {*} menuMoreWidth width that the "More" label takes
 * @returns Object containing arrays: { priorityLinks, menuLinks }
 */
const groupMeasuredLinks = (links, containerWidth, menuMoreWidth) => {
  const isMeasured = !!links?.[0]?.width && menuMoreWidth > 0;
  const hasNoPrimaryLinks = !links.find(l => l.group === 'primary');
  // - We can't calculate groups, if the width of the rendered links are not measured
  // - We don't need to calculate groups, if links don't contain any 'primary' links
  if (!isMeasured || hasNoPrimaryLinks) {
    return { priorityLinks: [], menuLinks: links };
  }

  // When every link is primary and the whole row fits, no "More" label is needed. The greedy
  // pass below reserves the "More" width for every link but the last, so it would move links
  // into the menu even when all of them fit.
  if (isWholeRowFitting(links, containerWidth)) {
    return { priorityLinks: links, menuLinks: [] };
  }

  const groupedLinks = links.reduce(
    (pickedLinks, link, i) => {
      const isPrimary = link.group === 'primary';
      const isLast = i === links.length - 1;
      // Has menuLinks at this point of the iteration (seconary links are at the end of the array)
      const hasMenuLinks = pickedLinks.menuLinks?.length > 0;

      const linksInRow = i + 1;
      const hasSpace =
        isLast && !hasMenuLinks
          ? link.cumulatedWidth + linksInRow * ROUNDING_ALLOWANCE_PER_ITEM <= containerWidth
          : link.cumulatedWidth + menuMoreWidth + (linksInRow + 1) * ROUNDING_ALLOWANCE_PER_ITEM <=
            containerWidth;

      return isPrimary && hasSpace
        ? {
            priorityLinks: [...pickedLinks.priorityLinks, link],
            menuLinks: pickedLinks.menuLinks,
          }
        : {
            priorityLinks: pickedLinks.priorityLinks,
            menuLinks: [...pickedLinks.menuLinks, link],
          };
    },
    { priorityLinks: [], menuLinks: [] }
  );
  return groupedLinks;
};

// Inner width of the element that lays out the topbar items (TopbarDesktop's navContainer).
// The body width overestimates it: the topbar's side padding sits on the outer <nav>, not on
// this element, so the space was overestimated by 24px and the "More" label could overflow.
const innerWidth = node => {
  if (!node) {
    return 0;
  }
  const style = window.getComputedStyle(node);
  const padding = (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0);
  return renderedWidth(node) - padding;
};

const calculateContainerWidth = (containerRefTarget, reservedWidth = 0) => {
  // Siblings include logo, search form, (inbox, profile menu || login signup)
  const siblingArray = containerRefTarget?.parentNode?.childNodes
    ? Array.from(containerRefTarget.parentNode.childNodes).filter(n => n !== containerRefTarget)
    : [];
  const siblingWidthsCombined = siblingArray.reduce((acc, node) => acc + renderedWidth(node), 0);

  // We figure out available width from the parent (TopbarDesktop's navContainer) and siblings.
  // reservedWidth accounts for content that now lives *inside* this container
  // (e.g. categoriesCta) but still needs to be excluded from the space left
  // over for the measured priority/menu links.
  const availableContainerWidth =
    innerWidth(containerRefTarget?.parentElement) - siblingWidthsCombined - reservedWidth;
  return availableContainerWidth;
};

/**
 * Create CustomLinksMenu component that takes all the extra space from the TopbarDesktop and
 * renders primary links directly there if there's enough space - and secondary links are shown inside a "More" menu.
 * If the space is limited, primary links as well as secondary links are shown inside the "More" menu.
 *
 * Note: this component is inherently a bit fragile as it needs to deal with DOM directly. If you customize TopbarDesktop,
 * test the responsiveness thoroughly.
 *
 * props:
 * - customLinks: array of link configurations.
 * - hasClientSideContentReady: indicates if TopbarDesktop is ready to render
 * - currentNav: the open page (currentNavItem.getCurrentNav), for the current-page indicator.
 * - intl: React Intl instance
 *
 * @param {*} props contains currentNav, customLinks, intl, and hasClientSideContentReady
 * @returns component to be placed inside TopbarDesktop
 */
const CustomLinksMenu = ({
  currentNav,
  customLinks = [],
  hasClientSideContentReady,
  intl,
  showCreateListingsLink,
  postListingLabelId,
  categoriesCta,
}) => {
  const containerRef = useRef(null);
  const categoriesCtaRef = useRef(null);
  const observer = useRef(null);
  const [mounted, setMounted] = useState(hasCustomLinksMenuHydratedOnce);
  const [moreLabelWidth, setMoreLabelWidth] = useState(0);

  // Stable signature of the current link composition, used to key the cache.
  // A mismatch means the set of visible links has changed and the cache must not be reused.
  // Includes all layout-relevant fields (not just href) so internal route links
  // and the secondaryCta modifier can't reuse stale measured widths.
  const customLinksSignature = JSON.stringify(
    customLinks.map(l => ({
      text: l.text,
      type: l.type,
      href: l.href,
      group: l.group,
      routeName: l.route?.name,
      routeParams: l.route?.params,
      routeTo: l.route?.to,
      highlight: l.highlight,
      secondaryCta: l.secondaryCta,
    }))
  );
  const cacheKey = `${showCreateListingsLink ? '1' : '0'}:${postListingLabelId || ''}:${customLinksSignature}`;

  const freshLinks = [
    ...createListingLinkConfigMaybe(intl, showCreateListingsLink, postListingLabelId),
    ...customLinks,
  ];
  // A cached measurement is reused only if it was taken with the web fonts loaded.
  const initialLinks =
    measuredLinksCache?.key === cacheKey && measuredLinksCache.fontsLoaded
      ? measuredLinksCache.links
      : freshLinks;
  const [links, setLinks] = useState(initialLinks);
  // Bumped to remount PriorityLinks and LinksMenu, which measure the link widths and the
  // "More" label once on mount.
  const [measureRound, setMeasureRound] = useState(0);
  const remeasureScheduled = useRef(false);
  const isUnmounted = useRef(false);

  const [layoutData, setLayoutData] = useState({
    priorityLinks: links,
    menuLinks: [],
    containerWidth: 0,
  });

  useEffect(() => {
    hasCustomLinksMenuHydratedOnce = true;
    setMounted(true);
    return () => {
      isUnmounted.current = true;
    };
  }, []);

  // If a measurement was taken while web fonts were loading, measure the links and the
  // "More" label again once the fonts are ready (the result then no longer depends on
  // load timing). Called right after a measurement, so the font status reflects it.
  const remeasureWhenFontsReady = () => {
    if (areFontsLoaded() || remeasureScheduled.current) {
      return;
    }
    remeasureScheduled.current = true;
    document.fonts.ready.then(() => {
      remeasureScheduled.current = false;
      if (isUnmounted.current) {
        return;
      }
      measuredLinksCache = null;
      setLinks(freshLinks);
      setLayoutData(prev => ({
        ...prev,
        priorityLinks: freshLinks,
        menuLinks: [],
        containerWidth: 0,
      }));
      setMoreLabelWidth(0);
      setMeasureRound(round => round + 1);
    });
  };

  // Wraps the state setter so every measured link array is also persisted to the
  // module-level cache, making it available for the next SPA remount.
  // The measured links never enter the row ungrouped (backlog #4 C4 / C9): the whole ungrouped
  // row overflowed the topbar for a frame, widened the page and moved its content.
  const setLinksAndCache = newLinks => {
    measuredLinksCache = { key: cacheKey, links: newLinks, fontsLoaded: areFontsLoaded() };
    setLinks(newLinks);
    remeasureWhenFontsReady();
  };

  // Measured links are grouped before the browser paints, with the computation the observer runs
  // a frame later: the row enters the topbar in the frame it was measured in, so the CTA beside it
  // moves once (a menu remounted after the user loads used to show an empty row first). A row that
  // fits whole needs no "More" width; otherwise this runs again once LinksMenu has measured «Ещё»
  // (also before the paint).
  useLayoutEffectOnClient(() => {
    const container = containerRef.current;
    if (!hasClientSideContentReady || !container || !links?.[0]?.width) {
      return;
    }
    const availableContainerWidth = calculateContainerWidth(
      container,
      renderedWidth(categoriesCtaRef.current)
    );
    const groupedLinks =
      moreLabelWidth > 0
        ? groupMeasuredLinks(links, availableContainerWidth, moreLabelWidth)
        : isWholeRowFitting(links, availableContainerWidth)
          ? { priorityLinks: links, menuLinks: [] }
          : null;
    if (groupedLinks) {
      const next = { ...groupedLinks, containerWidth: availableContainerWidth };
      setLayoutData(prev => (isSameLayout(prev, next) ? prev : next));
    }
  }, [links, hasClientSideContentReady, moreLabelWidth]);

  const setMeasuredMoreLabelWidth = width => {
    setMoreLabelWidth(width);
    remeasureWhenFontsReady();
  };

  useEffect(() => {
    let animationFrameId = null;
    const body = document.body;
    const container = containerRef.current;
    // The available width is the body width minus these elements (logo, search form, inbox,
    // profile menu or signup/login) and the categories CTA slot, so they are observed too:
    // their widths can settle after the first measurement (web fonts, user data).
    const layoutSiblings = [
      ...(container?.parentNode ? Array.from(container.parentNode.children) : []).filter(
        n => n !== container
      ),
      ...(categoriesCtaRef.current ? [categoriesCtaRef.current] : []),
    ];

    if (hasClientSideContentReady && moreLabelWidth > 0) {
      // ResizeObserver sets layout data: grouped priority links and links that go to menuLinks dropdown
      observer.current = new ResizeObserver(entries => {
        const containerRefParentWidth = container?.parentNode?.offsetWidth;
        const bodyOffsetWidth = body.offsetWidth;

        for (const entry of entries) {
          // Body's width has changed (aka viewport has changed)
          const isBodyTheTarget = entry.target === body;
          const isLayoutSiblingTheTarget = layoutSiblings.includes(entry.target);
          // If the width of the TopbarDesktop (aka <nav>) changes, this assumes that the document.body has the correct width.
          const hasWidthOfTopbarDesktopChanged =
            !isBodyTheTarget && containerRefParentWidth !== bodyOffsetWidth;

          if (isBodyTheTarget || isLayoutSiblingTheTarget || hasWidthOfTopbarDesktopChanged) {
            const target = container;
            const categoriesCtaWidth = renderedWidth(categoriesCtaRef.current);
            const availableContainerWidth = calculateContainerWidth(target, categoriesCtaWidth);

            // The groupedLinks variable contains { priorityLinks, menuLinks }
            const groupedLinks = groupMeasuredLinks(links, availableContainerWidth, moreLabelWidth);
            // The setLayoutData call affects the UI. This pushes the painting to the next frame.
            // An unchanged result keeps the previous state, so observing the siblings can't loop.
            animationFrameId = window.requestAnimationFrame(() => {
              if (container) {
                const next = { ...groupedLinks, containerWidth: availableContainerWidth };
                setLayoutData(prev => (isSameLayout(prev, next) ? prev : next));
              }
            });
            // After setLayoutData is called, don't process other entries
            break;
          }
        }
      });

      if (container) {
        // We need to observe both document body and the component's own container
        // When the window is squeezed, priority links prevent the container to shrink smaller.
        // At that point, we need to calculate the width from the width of the body.
        // It's also possible that some of the other elements get a repaint after window-level repaint (e.g. image loads)
        // In those cases, we just need to
        observer.current.observe(body);
        observer.current.observe(container);
        layoutSiblings.forEach(node => observer.current.observe(node));
      }
    }
    return () => {
      if (body) {
        observer.current?.unobserve(body);
      }
      if (container) {
        observer.current?.unobserve(container);
      }
      layoutSiblings.forEach(node => observer.current?.unobserve(node));
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, [containerRef, hasClientSideContentReady, moreLabelWidth, links]);

  const { priorityLinks, menuLinks, containerWidth } = layoutData;

  // If there are no custom links, just render createListing link.
  if (customLinks?.length === 0 && showCreateListingsLink) {
    return (
      <CreateListingMenuLink
        customLinksMenuClass={css.createListingLinkOnly}
        postListingLabelId={postListingLabelId}
        categoriesCta={categoriesCta}
      />
    );
  }

  const styleMaybe = mounted && containerWidth > 0 ? { style: { width: `${containerWidth}px` } } : {};
  const isMeasured = !!links?.[0]?.width;
  const hasPriorityLinks = isMeasured && priorityLinks.length > 0;

  return (
    <div className={css.customLinksMenu} ref={containerRef} {...styleMaybe}>
      <div className={css.categoriesCtaSlot} ref={categoriesCtaRef}>
        {categoriesCta}
      </div>
      <div className={css.measuredLinksGroup}>
        <PriorityLinks
          key={`links-${measureRound}`}
          links={links}
          priorityLinks={priorityLinks}
          isGrouped={containerWidth > 0}
          setLinks={setLinksAndCache}
          currentNav={currentNav}
        />
        {mounted ? (
          // Always mounted after hydration so MeasureMoreMenu can report
          // moreLabelWidth; the visible trigger is gated internally by menuLinks.
          // This breaks the grouping bootstrap deadlock (moreLabelWidth needed
          // grouping, which needed the trigger, which needed moreLabelWidth).
          // Remounted with PriorityLinks so MeasureMoreMenu measures the "More" label again
          // even when a reset to 0 is batched with a measurement in the same render.
          <LinksMenu
            key={`menu-${measureRound}`}
            id="linksMenu"
            currentNav={currentNav}
            links={menuLinks}
            showMoreLabel={hasPriorityLinks}
            moreLabelWidth={moreLabelWidth}
            setMoreLabelWidth={setMeasuredMoreLabelWidth}
            intl={intl}
          />
        ) : null}
      </div>
    </div>
  );
};

export default CustomLinksMenu;
